// 인증 스토어 — 바닐라의 checkAuth/doLogin/startTokenRefresh(2벌 복붙) 통합.
// 토큰은 기존 프론트와 같은 localStorage 'token' 키를 공유하므로,
// 바닐라(/)에서 로그인한 상태 그대로 /v2 에서도 인증된다.
import { create } from "zustand";
import { api, AuthError, clearToken, getToken, setToken } from "../lib/api";

export interface User {
  uid?: string;
  username?: string;
  name?: string;
  /** 서버가 판정해 내려준 값 — 메뉴 노출에만 쓴다.
   *  실제 권한은 관리자 API 가 매 요청 다시 본다(이 값은 신뢰 경계 밖). */
  is_admin?: boolean;
  /** 관리자 추가/제거 권한 (최고관리자) — 역시 서버 판정값 */
  is_manager?: boolean;
  /** 구글(베타) 로그인 사용자 — 서버가 내려준다. 엔터프라이즈 전용 메뉴(DCF·금융상품 평가)를 숨기는 데 쓴다 */
  beta?: boolean;
  [k: string]: unknown;
}

interface AuthState {
  user: User | null;
  checking: boolean;
  error: string;
  checkAuth: () => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  /** 이미 발급된 토큰으로 세션 시작 — 베타 구글 로그인이 쓴다 (베타 종료 시 제거) */
  loginWithToken: (token: string, user?: User) => void;
  logout: () => Promise<void>;
}

// ── 로컬 개발: 로그인 없이 화면 보기 ──────────────────────────────────
// 백엔드(:8000)를 안 띄우면 /agent/me 가 실패해 늘 로그인 오버레이가 덮인다 —
// 화면만 보려는데 매번 막히므로 vite dev 에서는 가짜 사용자로 통과시킨다.
//
// 프로덕션에 샐 수 없다: import.meta.env.DEV 는 빌드할 때 false 로 치환되고,
// 호출부를 `import.meta.env.DEV && …` 로 감싸 두면 분기 전체가 번들에서 사라진다
// (함수 안에서만 검사하면 죽은 코드인 걸 번들러가 증명하지 못해 문자열이 남는다 —
//  실제로 그렇게 짰다가 dist 에 남는 걸 확인하고 이 모양으로 고쳤다).
// 로그인 화면 자체를 확인하려면 주소에 ?login 을 붙인다 — 예: localhost:5173/?login
function devUser(): User {
  return {
    uid: "dev",
    username: "dev",
    name: "로컬 개발",
    // 관리자 메뉴는 열지 않는다 — 어차피 서버가 매 요청 권한을 다시 본다
    is_admin: false,
    is_manager: false,
  };
}

function wantsLoginScreen(): boolean {
  try {
    return new URLSearchParams(window.location.search).has("login");
  } catch {
    return false;
  }
}

let refreshTimer: ReturnType<typeof setInterval> | undefined;
// 로그인 직후에는 토큰 검증 전파 지연으로 재검증이 일시 실패할 수 있다 —
// 이 유예 시간 안의 checkAuth 실패는 세션을 지우지 않는다.
let lastLoginAt = 0;
const LOGIN_GRACE_MS = 90_000;

function startTokenRefresh(check: () => Promise<void>) {
  if (refreshTimer) clearInterval(refreshTimer);
  // 30분마다 /agent/me 재검증 — 만료 시 오버레이로 자연 복귀
  refreshTimer = setInterval(check, 30 * 60 * 1000);
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  checking: true,
  error: "",

  checkAuth: async () => {
    const hadToken = !!getToken();
    try {
      const d = await api<{ ok: boolean; user?: User }>("/agent/me");
      if (d.ok && d.user) {
        set({ user: d.user, checking: false, error: "" });
        startTokenRefresh(get().checkAuth);
        return;
      }
    } catch (e) {
      if (e instanceof AuthError && hadToken) {
        clearToken();
        if (refreshTimer) clearInterval(refreshTimer);
        lastLoginAt = 0;
        set({ user: null, checking: false, error: e.message });
        return;
      }
      /* 미로그인 또는 일시적 검증 실패 */
    }
    if (get().user && Date.now() - lastLoginAt < LOGIN_GRACE_MS) {
      set({ checking: false });   // 방금 로그인한 세션은 유지
      return;
    }
    if (import.meta.env.DEV && !wantsLoginScreen()) {
      console.info("[dev] 로그인 없이 화면을 띄운다. 로그인 화면을 보려면 ?login 을 붙일 것");
      set({ user: devUser(), checking: false });
      return;
    }
    set({ user: null, checking: false });
  },

  login: async (username, password) => {
    clearToken();
    if (refreshTimer) clearInterval(refreshTimer);
    lastLoginAt = 0;
    set({ user: null, error: "" });
    try {
      const d = await api<{ ok: boolean; token?: string; user?: User; message?: string }>(
        "/agent/login",
        { method: "POST", body: JSON.stringify({ username, password }) },
      );
      if (d.ok && d.token) {
        setToken(d.token);
        lastLoginAt = Date.now();
        set({ user: d.user || { username } });
        startTokenRefresh(get().checkAuth);
        return true;
      }
      set({ error: d.message || "로그인에 실패했습니다." });
      return false;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "로그인에 실패했습니다." });
      return false;
    }
  },

  loginWithToken: (token, user) => {
    setToken(token);
    lastLoginAt = Date.now();
    set({ user: user || null, error: "" });
    startTokenRefresh(get().checkAuth);
    if (!user) void get().checkAuth();
  },

  logout: async () => {
    try {
      await api("/agent/logout", { method: "POST" });
    } catch {
      /* 토큰 만료 등 — 로컬 정리는 계속 */
    }
    clearToken();
    if (refreshTimer) clearInterval(refreshTimer);
    set({ user: null });
  },
}));
